import { HugeiconsIcon } from '@hugeicons/react';
import {
  Alert01Icon,
  CancelCircleIcon,
  CheckListIcon,
  CheckmarkCircle02Icon,
  LinkSquare01Icon,
  Resize01Icon,
} from '@hugeicons/core-free-icons';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, RiskBadge, StatusBadge, EmptyState } from '@/components/common/Bits';
import { fmtDate } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';

const VOTE_OPTIONS = [
  'Aprobada para evaluación',
  'Aprobada para POC',
  'Aprobada para implementación',
  'Consolidar con otra',
  'Postergar',
  'Reformular',
  'Derivar a producto existente',
  'No continuar',
];

export default function Committee() {
  const { user, can } = useAuth();
  const [queue, setQueue] = useState(null);

  const load = () => api.get('/committee/queue').then(({ data }) => setQueue(data));
  useEffect(() => {
    load();
  }, []);

  if (!queue)
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-40 rounded-xl" />
        ))}
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <PageHeader
        title="Comité de IA"
        subtitle="Decision room — revisión del dossier, conflictos de interés, votación con quórum y registro de condiciones."
        testId="committee-header"
      />
      {queue.length === 0 && <EmptyState icon={Resize01Icon} title="Sin iniciativas en decisión" />}
      <StaggerContainer className="space-y-5" stagger={0.06}>
        {queue.map((d) => {
          const myVote = d.votes.find((v) => (v.user_id || v.voter_id) === user.id);
          const open = d.status === 'En votación';
          return (
            <StaggerItem key={d.id}>
              <Card className="shadow-card border-border rounded-2xl" data-testid={`decision-card-${d.initiative_id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={d.status} />
                        {d.initiative && <RiskBadge level={d.initiative.residual_risk} />}
                        {d.initiative?.clinical_data && (
                          <span className="text-[11px] px-2 py-0.5 rounded-md bg-risk-critical/10 text-risk-critical border border-risk-critical/25 font-medium">
                            Iniciativa clínica · aprobaciones independientes
                          </span>
                        )}
                      </div>
                      <Link
                        to={`/iniciativas/${d.initiative_id}`}
                        className="group inline-flex items-center gap-1.5 mt-2">
                        <h2 className="font-display text-lg font-semibold group-hover:text-primary transition-colors">
                          {d.initiative_name}
                        </h2>
                        <HugeiconsIcon icon={LinkSquare01Icon} className="h-3.5 w-3.5 text-muted-foreground" />
                      </Link>
                      <p className="text-xs text-muted-foreground mt-1">
                        {d.initiative?.department} · {d.initiative?.provider} · {d.initiative?.model}
                      </p>

                      <div className="mt-4 rounded-lg border border-border bg-gradient-panel p-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                          Estado del dossier
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <span className="flex items-center gap-1.5">
                            {d.dossier.items.assessments > 0 ? (
                              <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3.5 w-3.5 text-success" />
                            ) : (
                              <HugeiconsIcon icon={CancelCircleIcon} className="h-3.5 w-3.5 text-risk-high" />
                            )}{' '}
                            Evaluación ({d.dossier.items.assessments})
                          </span>
                          <span className="flex items-center gap-1.5">
                            {d.dossier.items.controls > 0 ? (
                              <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3.5 w-3.5 text-success" />
                            ) : (
                              <HugeiconsIcon icon={CancelCircleIcon} className="h-3.5 w-3.5 text-risk-high" />
                            )}{' '}
                            Controles ({d.dossier.items.controls})
                          </span>
                          <span className="flex items-center gap-1.5">
                            {d.dossier.items.documents > 0 ? (
                              <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3.5 w-3.5 text-success" />
                            ) : (
                              <HugeiconsIcon icon={CancelCircleIcon} className="h-3.5 w-3.5 text-risk-high" />
                            )}{' '}
                            Evidencias ({d.dossier.items.documents})
                          </span>
                        </div>
                        {!d.dossier.complete && (
                          <p className="text-xs text-risk-medium mt-2 flex items-center gap-1.5">
                            <HugeiconsIcon icon={Alert01Icon} className="h-3.5 w-3.5" /> {d.dossier.missing.join(' · ')}
                          </p>
                        )}
                      </div>

                      {d.conditions?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Condiciones registradas
                          </p>
                          <ul className="text-xs mt-1.5 space-y-1">
                            {d.conditions.map((c) => (
                              <li key={c} className="flex gap-1.5">
                                <HugeiconsIcon
                                  icon={CheckmarkCircle02Icon}
                                  className="h-3.5 w-3.5 text-risk-medium shrink-0 mt-0.5"
                                />
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="lg:w-80 shrink-0 flex flex-col">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Votos · quórum {d.votes.length}/{d.quorum_required}
                      </p>
                      <div className="space-y-2 flex-1">
                        {d.votes.length === 0 && (
                          <p className="text-xs text-muted-foreground">Aún no hay votos registrados.</p>
                        )}
                        {d.votes.map((v) => (
                          <div key={v.id} className="rounded-lg border border-border p-2.5">
                            <div className="flex justify-between items-center gap-2">
                              <p className="text-xs font-semibold truncate">{v.user_name || v.voter}</p>
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {fmtDate(v.created_at || v.date)}
                              </span>
                            </div>
                            <p className="text-[11px] text-primary font-medium mt-0.5">{v.vote}</p>
                            <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{v.justification}</p>
                          </div>
                        ))}
                      </div>
                      {open && can.vote && !myVote && d.dossier.complete && <VoteDialog decision={d} onVoted={load} />}
                      {open && can.vote && myVote && (
                        <p className="text-xs text-success mt-3 flex items-center gap-1.5">
                          <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3.5 w-3.5" /> Su voto ya fue
                          registrado
                        </p>
                      )}
                      {open && !can.vote && (
                        <p className="text-xs text-muted-foreground mt-3">Su rol no participa en la votación.</p>
                      )}
                      {open && can.vote && !d.dossier.complete && (
                        <p className="text-xs text-risk-medium mt-3">No se puede votar con dossier incompleto.</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </StaggerItem>
          );
        })}
      </StaggerContainer>
    </div>
  );
}

const VoteDialog = ({ decision, onVoted }) => {
  const [open, setOpen] = useState(false);
  const [vote, setVote] = useState('');
  const [justification, setJustification] = useState('');
  const [conditions, setConditions] = useState('');
  const [conflict, setConflict] = useState(false);
  const [conflictReason, setConflictReason] = useState('');
  const [consolidateWith, setConsolidateWith] = useState('');
  const [postponeUntil, setPostponeUntil] = useState('');
  const [reformulateReason, setReformulateReason] = useState('');
  const [deriveToProduct, setDeriveToProduct] = useState('');
  const [initiatives, setInitiatives] = useState([]);

  useEffect(() => {
    if (open && vote === 'Consolidar con otra') {
      api
        .get('/initiatives')
        .then(({ data }) => {
          setInitiatives((data?.initiatives || data || []).filter((i) => i.id !== decision.initiative_id));
        })
        .catch(() => {});
    }
  }, [open, vote, decision.initiative_id]);

  const declareConflict = async () => {
    if (!conflictReason) {
      toast.error('Describa el conflicto de interés');
      return;
    }
    try {
      const { data } = await api.post(`/committee/${decision.id}/conflict`, { reason: conflictReason });
      toast.info(data.message);
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async () => {
    try {
      const payload = {
        vote,
        justification,
        conditions: conditions.trim(),
        conflict_of_interest: conflict,
      };
      if (vote === 'Consolidar con otra') payload.consolidate_with_id = consolidateWith;
      if (vote === 'Postergar') payload.postpone_until = postponeUntil;
      if (vote === 'Reformular') payload.reformulate_reason = reformulateReason;
      if (vote === 'Derivar a producto existente') payload.derive_to_product = deriveToProduct;

      const { data } = await api.post(`/committee/${decision.id}/vote`, payload);
      toast.success(
        data.final_status
          ? `Quórum alcanzado · decisión: ${data.final_status}`
          : `Voto registrado (${data.votes_count}/${data.quorum_required})`,
      );
      setOpen(false);
      onVoted();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="mt-3 bg-primary hover:bg-primary/90 text-primary-foreground"
          data-testid={`vote-btn-${decision.initiative_id}`}>
          <HugeiconsIcon icon={CheckListIcon} className="h-4 w-4 mr-2" /> Emitir voto
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Votación · {decision.initiative_name}</DialogTitle>
          <DialogDescription>
            El fundamento es obligatorio. No es posible votar con conflicto de interés declarado.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">Declaro conflicto de interés</p>
              <p className="text-[11px] text-muted-foreground">Al declararlo quedará inhabilitado para votar</p>
            </div>
            <Switch checked={conflict} onCheckedChange={setConflict} data-testid="conflict-switch" />
          </div>

          {conflict ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Describa el conflicto</Label>
                <Textarea
                  rows={2}
                  value={conflictReason}
                  onChange={(e) => setConflictReason(e.target.value)}
                  data-testid="conflict-reason-input"
                />
              </div>
              <Button
                onClick={declareConflict}
                variant="outline"
                className="w-full border-risk-medium/40 text-risk-medium hover:bg-risk-medium/5"
                data-testid="declare-conflict-btn">
                Registrar abstención por conflicto
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Decisión</Label>
                <Select value={vote} onValueChange={setVote}>
                  <SelectTrigger data-testid="vote-select">
                    <SelectValue placeholder="Seleccionar voto" />
                  </SelectTrigger>
                  <SelectContent>
                    {VOTE_OPTIONS.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Fundamento (obligatorio)</Label>
                <Textarea
                  rows={3}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Fundamento de la decisión para el registro de auditoría"
                  data-testid="vote-justification-input"
                />
              </div>
              {vote === 'Consolidar con otra' && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Iniciativa con la que se consolida</Label>
                  <Select value={consolidateWith} onValueChange={setConsolidateWith}>
                    <SelectTrigger data-testid="consolidate-select">
                      <SelectValue placeholder="Seleccionar iniciativa" />
                    </SelectTrigger>
                    <SelectContent>
                      {initiatives.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.code || i.id} — {i.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {vote === 'Postergar' && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Postergar hasta</Label>
                  <Input
                    type="date"
                    value={postponeUntil}
                    onChange={(e) => setPostponeUntil(e.target.value)}
                    data-testid="postpone-date-input"
                  />
                </div>
              )}
              {vote === 'Reformular' && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Razón de la reformulación</Label>
                  <Textarea
                    rows={2}
                    value={reformulateReason}
                    onChange={(e) => setReformulateReason(e.target.value)}
                    placeholder="Qué debe reformularse y por qué"
                    data-testid="reformulate-reason-input"
                  />
                </div>
              )}
              {vote === 'Derivar a producto existente' && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Nombre del producto existente</Label>
                  <Input
                    value={deriveToProduct}
                    onChange={(e) => setDeriveToProduct(e.target.value)}
                    placeholder="Producto o capacidad al que se deriva"
                    data-testid="derive-product-input"
                  />
                </div>
              )}
              {(vote === 'Aprobada para evaluación' ||
                vote === 'Aprobada para POC' ||
                vote === 'Aprobada para implementación') && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Condiciones (una por línea)</Label>
                  <Textarea
                    rows={2}
                    value={conditions}
                    onChange={(e) => setConditions(e.target.value)}
                    placeholder="Condiciones opcionales para la aprobación"
                    data-testid="vote-conditions-input"
                  />
                </div>
              )}
            </>
          )}
        </div>
        {!conflict && (
          <DialogFooter>
            <Button
              onClick={submit}
              disabled={!vote}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="vote-submit-btn">
              Registrar voto
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
